import React, { useEffect } from 'react';
import { MButton } from '@/components/ui/atoms/MButtons';
import { motion, AnimatePresence } from 'framer-motion';
import FooterDrawerDoneButton from '@/components/ui/atoms/FooterDrawerDoneButton';
import '@/components/ui/atoms/footer-drawer-done-button.css';
import { IconX } from '@tabler/icons-react';
import styles from './FooterDrawer.module.css';
import DrawerPortal from './DrawerPortal';
import MWrap from '@/components/ui/atoms/MWrap';


interface FooterDrawerProps {
  open: boolean;
  onClose: () => void;
  showClose?: boolean;
  children: React.ReactNode;
  doneDisabled?: boolean;
  onDone?: () => void;
  doneText?: string;
  donePosition?: 'top' | 'bottom';
  title?: string;
  /**
   * Height of the drawer (e.g. 'auto', '400px', '80%').
   * If not provided, defaults to maxHeight: '85vh'.
   */
  height?: string;
  className?: string;
}

const backdropVariants = {
  visible: { opacity: 1, pointerEvents: 'auto' as const },
  hidden: { opacity: 0, pointerEvents: 'none' as const },
};

const drawerVariants = {
  hidden: { y: '100%' },
  visible: { y: 0 },
};

const FooterDrawer: React.FC<FooterDrawerProps> = ({ open, onClose, showClose = true, children, doneDisabled, onDone, doneText, donePosition, title, height, className }) => {
  // Detect Android device
  const isAndroid = /Android/i.test(navigator.userAgent);
  // Prevent keyboard from opening on mobile devices
  useEffect(() => {
    if (open) {
      // Blur any focused element to prevent keyboard from showing
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      
      // Add a class to the body to prevent scrolling and further interactions
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }
    
    return () => {
      document.body.classList.remove('drawer-open');
    };
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <DrawerPortal>
          {/* Backdrop */}
          <motion.div
            className={styles.backdrop}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            onClick={onClose}
          />
          {/* Drawer */}
          <motion.div
            className={`${styles.drawer} ${className || ''}`}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={drawerVariants}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={height ? { height } : { maxHeight: '85vh' }}
          >
            {/* Header */}
            <div className={styles.header}>
              {/* Drag bar */}
              <div className={styles.dragBar} />
              <div className={styles.headerContent}>
                {/* Left side: Close button if done button exists, otherwise spacer */}
                {onDone && showClose ? (
                  <button
                    className={styles.closeButton}
                    onClick={e => { e.preventDefault(); onClose(); }}
                    aria-label="Close"
                  >
                    <IconX size={24} />
                  </button>
                ) : (
                  <div className={styles.spacer} />
                )}
                {/* Title in center */}
                {title && (
                  <span className={styles.title}>
                    {typeof title === 'string' ? <MWrap text={title} maxChars={30} /> : title}
                  </span>
                )}
                {/* Right side: Done button if exists, otherwise close button */}
                {onDone ? (
                  <div className={styles.doneButton}>
                    <MButton
                      variant="link"
                      size="md"
                      text={typeof doneText === 'string' ? doneText : 'Done'}
                      state="default"
                      style={{ padding: 0, background: 'none', fontWeight: 600, fontSize: 'var(--typography-font-size-body-base)' }}
                      onClick={onDone}
                      className="clear"
                      disabled={!!doneDisabled}
                    />
                  </div>
                ) : showClose ? (
                  <button
                    className={styles.closeButton}
                    onClick={e => { e.preventDefault(); onClose(); }}
                    aria-label="Close"
                  >
                    <IconX size={24} />
                  </button>
                ) : (
                  <div className={styles.spacer} />
                )}
              </div>
            </div>
            {/* Content */}
            <div className={styles.content} style={{ paddingBottom: isAndroid ? '40px' : '15px' }}>
              {children}
              {/* Done button pinned to bottom, only if onDone is provided and donePosition is not 'top' */}
              {onDone && donePosition !== 'top' && (
                <div className="w-full bg-white border-t border-gray-200 p-4">
                  <FooterDrawerDoneButton
                    disabled={doneDisabled}
                    onClick={onDone}
                  >
                    {typeof doneText === 'string' ? doneText : 'Done'}
                  </FooterDrawerDoneButton>
                </div>
              )}
            </div>
          </motion.div>
        </DrawerPortal>
      )}
    </AnimatePresence>
  );
};

export default FooterDrawer;
export { FooterDrawerDoneButton };
